import { Query } from '@apollo/client/react/components';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import React from 'react';

import {
  VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery,
  VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryVariables,
  VirtualConnectionTableLineageUpstreamDataQualityWarningsDocument
} from '../../../../../gen-src/queries';
import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  WarningStatusChip,
  WarningStatusProps
} from '../../../data-catalog-shared';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { RenderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';
import { EmbeddableDataQualityWarningProps } from './EmbeddableDataQualityWarningProps';

interface LineageWarningVirtualConnectionTableProps {
  readonly asset: AssetMarkedByIndicator;
  readonly vizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddableVirtualConnectionTableDataQualityWarningProps = EmbeddableDataQualityWarningProps & LineageWarningVirtualConnectionTableProps;

/** @deprecated TFS1478812 */
export function EmbeddableVirtualConnectionTableDataQualityWarning(props: EmbeddableVirtualConnectionTableDataQualityWarningProps): JSX.Element {
  const { source, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery, VirtualConnectionTableLineageUpstreamDataQualityWarningsQueryVariables>
          query={VirtualConnectionTableLineageUpstreamDataQualityWarningsDocument}
          variables={{ vizportalUrlId: props.vizportalUrlId, first: 10, offset: 0 }}
        >
          {result => <RenderQueryResult result={result} renderers={{
            renderLoading: () => renderWarnings(urls, { ...props }, source),
            renderData: data => renderWarnings(urls, { ...props }, source, data),
          }} />}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddableVirtualConnectionTableDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  source?: string,
  data?: VirtualConnectionTableLineageUpstreamDataQualityWarningsQuery
): JSX.Element | null {
  const virtualConnectionTable = data && data.virtualConnectionTables ? data.virtualConnectionTables[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive)
  if (!virtualConnectionTable && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = virtualConnectionTable
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(virtualConnectionTable.id, virtualConnectionTable.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings

  const props: WarningStatusProps = {
    subjectArea: 'VirtualConnectionTable',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
    source: source ?? 'EmbeddableVirtualConnectionTableDataQualityWarning',
  }

  return <WarningStatusChip {...props} />
}
